import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, X, Loader2, Mails, Trash2, Mail, CheckCircle2, MinusCircle, Download,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { cn } from '../lib/cn';

type Subscriber = {
  id: string;
  email: string;
  unsubscribedAt: string | null;
  createdAt: string;
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });

export function AdminNewsletter() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'newsletter-subscribers'],
    queryFn: () => api<{ subscribers: Subscriber[] }>('/api/admin/newsletter/subscribers'),
    refetchInterval: 60_000,
  });

  const subscribers = data?.subscribers ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/api/admin/newsletter/subscribers/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'newsletter-subscribers'] }),
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo eliminar el suscriptor.'),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subscribers;
    return subscribers.filter((s) => s.email.toLowerCase().includes(q));
  }, [subscribers, search]);

  const active = subscribers.filter((s) => !s.unsubscribedAt).length;
  const unsubscribed = subscribers.length - active;

  function exportCsv() {
    const rows = [
      ['email', 'estado', 'fecha_alta'],
      ...subscribers.map((s) => [
        s.email,
        s.unsubscribedAt ? 'baja' : 'suscrito',
        new Date(s.createdAt).toISOString(),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-suscriptores-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      {/* header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mails size={22} className="text-brand-600" /> Newsletter
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Suscriptores del blog. Reciben el resumen diario de artículos a las 09:00.
          </p>
        </div>
        {subscribers.length > 0 && (
          <button onClick={exportCsv} className="btn-secondary gap-2">
            <Download size={15} /> Exportar CSV
          </button>
        )}
      </div>

      {/* counts */}
      {!isLoading && subscribers.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5 bg-slate-100 text-slate-600">
            <Mail size={13} /> Total: {subscribers.length}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5 bg-emerald-100 text-emerald-700">
            <CheckCircle2 size={13} /> Suscritos: {active}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium rounded-lg px-2.5 py-1.5 bg-slate-100 text-slate-500">
            <MinusCircle size={13} /> Bajas: {unsubscribed}
          </span>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}

      {/* search */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
        <input
          type="text"
          placeholder="Buscar por email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-9 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent shadow-sm"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin" /> Cargando suscriptores…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 flex flex-col items-center justify-center gap-2 text-slate-400">
            <Mails size={28} strokeWidth={1.5} />
            <p className="text-sm">
              {search ? 'Sin resultados para esa búsqueda.' : 'Aún no hay suscriptores a la newsletter.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider hidden sm:table-cell">
                  Estado
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider hidden md:table-cell">
                  Fecha de alta
                </th>
                <th className="px-4 py-3.5 text-right text-xs font-medium text-slate-400 uppercase tracking-wider w-16" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => (
                <tr
                  key={s.id}
                  className={cn(
                    i < filtered.length - 1 && 'border-b border-slate-50',
                    'hover:bg-slate-50 transition-colors',
                  )}
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        <Mail size={14} className="text-slate-400" />
                      </div>
                      <span className="text-slate-700 truncate max-w-[260px] font-medium">{s.email}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    {s.unsubscribedAt ? (
                      <span className="inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-500">
                        Baja
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-emerald-100 text-emerald-700">
                        Suscrito
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 hidden md:table-cell">
                    <span className="text-slate-500 text-xs">{fmtDate(s.createdAt)}</span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <button
                      onClick={() => {
                        if (confirm(`¿Eliminar a ${s.email} de la lista?`)) deleteMutation.mutate(s.id);
                      }}
                      title="Eliminar suscriptor"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {search && !isLoading && (
        <p className="text-xs text-slate-400">
          {filtered.length} resultado{filtered.length !== 1 ? 's' : ''} para «{search}»
        </p>
      )}
    </div>
  );
}
